import { EFinancingProgram } from '@if/core/types'
import PageSpinner from '@src/components/PageSpinner'
import { useUser, useUserPermissions } from '@src/contexts/AppContext'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import useFeatureFlags from '@src/hooks/config-hooks'
import type { JSX } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import BrowseCreditApplicationPage from '../BrowseCreditApplicationPage'
import CalculatorPage from '../CalculatorPage'
import ChatDashboardPage from '../ChatDashboardPage'
import ChatPage from '../ChatPage'
import WorksheetWrapper from '../CreateWorksheetPage/WorksheetWrapper'
import CreateCreditApplicationPage from '../CreditApplicationPage/CreateCreditApplicationPage'
import CustomBuydownPage from '../CustomBuydownPage'
import EditCreditApplicationPage from '../CreditApplicationPage/EditCreditApplicationPage'
import FundingSummary from '../FundingSummary'
import ManageUsersPage from '../ManageUsersPage'
import ReportSettingsPage from '../ReportSettingsPage'
import ViewCreditApplicationPage from '../ViewCreditApplicationPage'
import RouteGuard from './components/RouteGuard'
import Statistics from './Statistics'
import TemplateApp from './TemplateApp'

const DetailCreditApplicationSubRouter = ({ financingProgramId }: { financingProgramId: EFinancingProgram }) => {
  const { id } = useParams<{ id: string }>()

  if (!id) return <Navigate to="/" />
  return (
    <Routes>
      <Route path="" element={<ViewCreditApplicationPage financingProgramId={financingProgramId} id={id} />} />
      <Route path="edit" element={<EditCreditApplicationPage financingProgramId={financingProgramId} id={id} />} />
      <Route path="worksheet" element={<WorksheetWrapper financingProgramId={financingProgramId} id={id} />} />
      <Route path="funding-summary" element={<FundingSummary financingProgramId={financingProgramId} id={id} />} />
    </Routes>
  )
}

const CreditApplicationSubRouter = () => {
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const { hasDetailAccess, canCreateApplications } = useUserPermissions()
  if (!financingProgramId) return <Navigate to="/" />
  return (
    <Routes>
      <Route
        path="add"
        element={<RouteGuard can={canCreateApplications} element={<CreateCreditApplicationPage />} />}
      />

      <Route
        path=":id/*"
        element={
          <RouteGuard
            can={hasDetailAccess}
            element={<DetailCreditApplicationSubRouter financingProgramId={financingProgramId} />}
          />
        }
      />
    </Routes>
  )
}

const ManageUsersSubRouter = () => {
  const { isAdmin } = useUserPermissions()
  if (!isAdmin) return <Navigate to="/" />

  return (
    <Routes>
      <Route path="" element={<RouteGuard can={isAdmin} element={<ManageUsersPage />} />} />
    </Routes>
  )
}

const ReportSettingsSubRouter = () => {
  const user = useUser()
  const { flags } = useFeatureFlags()
  const allowed = user?.rights.canSetReportProperties && !!flags.UseMerchantReportSettingsMenu
  if (!allowed) return <Navigate to="/" />

  return (
    <Routes>
      <Route path="" element={<RouteGuard can={allowed} element={<ReportSettingsPage />} />} />
    </Routes>
  )
}
const CustomBuydownSubRouter = () => {
  const user = useUser()
  const { flags } = useFeatureFlags()
  const [merchant, isFetching] = useMerchantById(user?.merchantId ?? '')
  const baseAllowed = !!user?.rights.canManagePromotions && !!flags.UseCustomFlashPlan
  if (baseAllowed && isFetching && !merchant) return <PageSpinner isLoading />
  const allowed = baseAllowed && !!merchant?.flashPlanDefinedInManageMerchant
  if (!allowed) return <Navigate to="/" />

  return (
    <Routes>
      <Route path="" element={<RouteGuard can={allowed} element={<CustomBuydownPage />} />} />
    </Routes>
  )
}

const TopLevelRoutes = (): JSX.Element => {
  return (
    <Routes>
      <Route element={<TemplateApp />}>
        <Route path="/credits" element={<BrowseCreditApplicationPage />} />
        <Route path="/loan-calculator" element={<CalculatorPage />} />
        <Route path="/credits/:financingProgramId/*" element={<CreditApplicationSubRouter />} />
        <Route path="/manage-users" element={<ManageUsersSubRouter />} />
        <Route path="/report-settings" element={<ReportSettingsSubRouter />} />
        <Route path="/custom-buydown" element={<CustomBuydownSubRouter />} />
        <Route path="/merchant-statistics" element={<Statistics />} />
        <Route path="/chat-dashboard" element={<ChatDashboardPage />} />
        <Route path="/chat-dashboard/:financingProgramId/:creditApplicationId" element={<ChatPage />} />
      </Route>
      <Route path="/" element={<Navigate to="/credits" replace />} />
      <Route path="/auth-page" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/credits" replace />} />
    </Routes>
  )
}

export default TopLevelRoutes
